import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../constants';

export default function useComptabilite({ getAuthHeader }) {
  const [comptaDateDebut, setComptaDateDebut] = useState('');
  const [comptaDateFin, setComptaDateFin] = useState('');
  const [comptaPaymentRef, setComptaPaymentRef] = useState('');
  const [comptaStats, setComptaStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [exportLoading, setExportLoading] = useState({});

  const fetchComptaStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      let url = `${API}/domium/export/statistiques`;
      const params = [];
      if (comptaDateDebut) params.push(`date_debut=${comptaDateDebut}`);
      if (comptaDateFin) params.push(`date_fin=${comptaDateFin}`);
      if (params.length > 0) url += '?' + params.join('&');
      const res = await axios.get(url, { headers: getAuthHeader() });
      setComptaStats(res.data);
    } catch { toast.error('Erreur lors du chargement des statistiques'); }
    finally { setLoadingStats(false); }
  }, [comptaDateDebut, comptaDateFin, getAuthHeader]);

  const handleExport = async (type, format = 'csv') => {
    setExportLoading(prev => ({ ...prev, [type]: true }));
    try {
      let url = `${API}/domium/export/${type}/${format}`;
      const params = [];
      if (comptaDateDebut) params.push(`date_debut=${comptaDateDebut}`);
      if (comptaDateFin) params.push(`date_fin=${comptaDateFin}`);
      if (params.length > 0) url += '?' + params.join('&');
      const response = await axios.get(url, { headers: getAuthHeader(), responseType: 'blob' });
      const ext = format === 'csv' ? 'csv' : 'xlsx';
      const mime = format === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([response.data], { type: mime });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a'); link.href = downloadUrl;
      link.download = `export_${type}_${new Date().toISOString().split('T')[0]}.${ext}`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success(`Export ${type} telecharge`);
    } catch { toast.error(`Erreur lors de l'export ${type}`); }
    finally { setExportLoading(prev => ({ ...prev, [type]: false })); }
  };

  const handleExportExcelComplet = async () => {
    setExportLoading(prev => ({ ...prev, excel: true }));
    try {
      let url = `${API}/domium/export/excel-complet`;
      const params = [];
      if (comptaDateDebut) params.push(`date_debut=${comptaDateDebut}`);
      if (comptaDateFin) params.push(`date_fin=${comptaDateFin}`);
      if (params.length > 0) url += '?' + params.join('&');
      const response = await axios.get(url, { headers: getAuthHeader(), responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a'); link.href = downloadUrl;
      link.download = `export_comptable_complet_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Export Excel complet telecharge');
    } catch { toast.error("Erreur lors de l'export Excel"); }
    finally { setExportLoading(prev => ({ ...prev, excel: false })); }
  };

  const setPeriode = (type) => {
    const now = new Date();
    switch (type) {
      case 'mois': {
        const debut = new Date(now.getFullYear(), now.getMonth(), 1);
        const fin = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        setComptaDateDebut(debut.toISOString().split('T')[0]);
        setComptaDateFin(fin.toISOString().split('T')[0]);
        break;
      }
      case 'trimestre': {
        const trimestre = Math.floor(now.getMonth() / 3);
        const debut = new Date(now.getFullYear(), trimestre * 3, 1);
        const fin = new Date(now.getFullYear(), (trimestre + 1) * 3, 0);
        setComptaDateDebut(debut.toISOString().split('T')[0]);
        setComptaDateFin(fin.toISOString().split('T')[0]);
        break;
      }
      case 'annee':
        setComptaDateDebut(new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0]);
        setComptaDateFin(new Date(now.getFullYear(), 11, 31).toISOString().split('T')[0]);
        break;
      case 'tout':
        setComptaDateDebut(''); setComptaDateFin('');
        break;
      default: break;
    }
  };

  return {
    comptaDateDebut, setComptaDateDebut, comptaDateFin, setComptaDateFin,
    comptaPaymentRef, setComptaPaymentRef, comptaStats, loadingStats,
    exportLoading, fetchComptaStats, handleExport, handleExportExcelComplet, setPeriode,
  };
}
